import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import { Tooltip } from '@web/atoms/Tooltip';
import {
  Card,
  CardLoadingOverlay,
  Info,
  StyledImage,
  Name,
  Description,
  CardContent,
  CardHeader,
} from './styles.js';

export const ImageCard = ({
  label,
  title,
  description,
  checked = false,
  img,
  onChange,
  disabled,
  isLoading = false,
  unavailable,
  unavailableText,
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  level,
  textDirection = 'column',
  titleColor = 'text.primary',
  descriptionColor = 'text.secondary',
  checkedBackgroundColor = 'background.checked',
  borderColor,
  checkedBorderColor = 'secondary.plum',
  totalCardCount,
  variant = 'long',
}) => {
  const hasImage = Boolean(img?.src);

  const getImageCard = props => (
    <Card
      {...props}
      checked={checked}
      $unavailable={unavailable}
      $disabled={disabled}
      $notSelectable={notSelectable}
      $hasImage={hasImage}
      $totalCardCount={totalCardCount}
      aria-label={`${label} ${title}`}
      aria-busy={isLoading || undefined}
      onClick={notSelectable || disabled || isLoading ? undefined : onChange}
      $checkedBackgroundColor={checkedBackgroundColor}
      $borderColor={borderColor}
      $checkedBorderColor={checkedBorderColor}
      $variant={variant}
    >
      <CardLoadingOverlay isLoading={isLoading} spinnerVariant="dark">
        <CardContent $hasImage={hasImage} $variant={variant}>
          {hasImage && (
            <StyledImage
              checked={checked}
              unavailable={disabled || unavailable}
            >
              <NextImage src={img.src} alt={img.alt} width={82} height={82} />
            </StyledImage>
          )}
          <Info $textDirection={textDirection} $hasImage={hasImage}>
            <CardHeader>
              <Name as={level} $titleColor={titleColor}>
                {title}
              </Name>
            </CardHeader>
            <Description
              $textDirection={textDirection}
              $descriptionColor={descriptionColor}
              $hasImage={hasImage}
            >
              {unavailable ? unavailableText : description}
            </Description>
          </Info>
        </CardContent>
      </CardLoadingOverlay>
    </Card>
  );

  return (notSelectable && !unavailable) || unavailable ? (
    <Tooltip trigger={getImageCard} placement="top" offsetY={-200}>
      {notSelectableText}
    </Tooltip>
  ) : (
    getImageCard()
  );
};

ImageCard.displayName = 'ImageCard';
ImageCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  textDirection: PropTypes.oneOf(['row', 'column']),
  titleColor: PropTypes.string,
  descriptionColor: PropTypes.string,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  unavailableLabel: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  checkedBackgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  checkedBorderColor: PropTypes.string,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  totalCardCount: PropTypes.number,
  variant: PropTypes.oneOf(['long', 'short']),
};
